import React, { cloneElement, useState } from 'react';
import { TopToolbar, sanitizeListRestProps, Button } from 'react-admin';

import {
  Button as MuiButton,
  Divider,
  Menu,
  MenuItem,
  useMediaQuery,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import MoreVertIcon from '@material-ui/icons/MoreVert';

const useStyles = makeStyles(() => ({
  menuItemMobile: {
    minHeight: 28,
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 10,
    paddingRight: 10,
    fontSize: '0.75rem',
    '& .MuiSvgIcon-root': {
      marginRight: 8,
      fontSize: '1.125rem',
    },
  },
}));

/**
 * Quando `maxAcoesVisiveis` é informado e `actions` excede esse limite, as primeiras
 * ficam como botões normais e o restante é agrupado num dropdown "Mais ações" (mesmo
 * padrão usado pelas ações em massa da seleção, com o ícone de 3 pontinhos verticais à
 * esquerda do texto e a setinha à direita). Sem `maxAcoesVisiveis`, mostra todas as ações
 * como sempre (comportamento padrão, usado pelas outras telas que reaproveitam este
 * componente).
 */
const MenuMaisAcoes = ({ acoes, label }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const classes = useStyles();
  const theme = useTheme();
  // no mobile, o menu abre colado embaixo do botão (em vez de sobrepor ele, que é o
  // posicionamento padrão do MUI) e os itens ficam mais compactos (`dense`).
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      <MuiButton
        size="small"
        startIcon={<MoreVertIcon />}
        endIcon={<ArrowDropDownIcon />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        {label}
      </MuiButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        {...(isMobile
          ? {
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              getContentAnchorEl: null,
            }
          : {})}
      >
        {acoes.map(({ label, onClick, icon }, index) => [
          index > 0 && <Divider key={`${label}-divisor`} />,
          <MenuItem
            key={label}
            dense={isMobile}
            className={isMobile ? classes.menuItemMobile : ''}
            onClick={() => {
              onClick();
              setAnchorEl(null);
            }}
          >
            {icon}
            {label}
          </MenuItem>,
        ])}
      </Menu>
    </>
  );
};

const ListActions = ({
  currentSort,
  className,
  resource,
  filters,
  displayedFilters,
  exporter,
  filterValues,
  permanentFilter,
  hasCreate,
  basePath,
  selectedIds,
  onUnselectItems,
  showFilter,
  maxResults,
  total,
  handleCriar,
  actions = [],
  maxAcoesVisiveis,
  labelMaisAcoes = 'Mais ações',
  ...rest
}) => {
  const temLimite =
    typeof maxAcoesVisiveis === 'number' && actions.length > maxAcoesVisiveis;
  const acoesVisiveis = temLimite
    ? actions.slice(0, maxAcoesVisiveis)
    : actions;
  const acoesNoMenu = temLimite ? actions.slice(maxAcoesVisiveis) : [];

  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {filters &&
        cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button',
        })}
      {handleCriar ? (
        <Button onClick={handleCriar} label="Novo">
          <AddIcon />
        </Button>
      ) : null}
      {acoesVisiveis.map(({ label, onClick, icon }) => (
        <Button key={label} onClick={onClick} label={label}>
          {icon}
        </Button>
      ))}
      {acoesNoMenu.length > 0 && (
        <MenuMaisAcoes acoes={acoesNoMenu} label={labelMaisAcoes} />
      )}
    </TopToolbar>
  );
};

export default ListActions;
